import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, useTheme } from '@mui/material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import type { ReviewHistoryEntry, ReviewRating } from '../../types/api.types';
import { computeReviewIntervals, MIN_INTERVAL_DAYS } from '../../utils/reviewIntervals';

interface ReviewIntervalChartProps {
  createdAt: string;
  history: ReviewHistoryEntry[];
  reviewCount: number;
}

const Y_TICKS = [1, 7, 30, 90, 365];

/**
 * Acquisition-progress graph: days between reviews (log scale) by review number.
 * A word that is sticking climbs; one that isn't keeps dropping back to the bottom.
 */
export function ReviewIntervalChart({ createdAt, history, reviewCount }: ReviewIntervalChartProps) {
  const { t } = useTranslation('vocabulary');
  const theme = useTheme();

  const points = useMemo(() => computeReviewIntervals(createdAt, history), [createdAt, history]);

  const ratingColor: Record<ReviewRating, string> = {
    again: theme.palette.error.main,
    hard: theme.palette.warning.main,
    medium: theme.palette.info.main,
    easy: theme.palette.success.main,
  };

  const hasPartialHistory = reviewCount > history.length;
  const yMax = Math.max(Y_TICKS[Y_TICKS.length - 1], ...points.map((p) => Math.ceil(p.days)));

  return (
    <Box data-testid="review-interval-chart" sx={{ width: '100%' }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mb: 0.5 }}>
        {t('reviewPage.intervalChart.title')}
      </Typography>

      {points.length < 2 ? (
        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', textAlign: 'center', py: 3 }}>
          {t('reviewPage.intervalChart.notEnough')}
        </Typography>
      ) : (
        <Box sx={{ width: '100%', height: 160 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={points} margin={{ top: 8, right: 12, bottom: 0, left: -12 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
              <XAxis dataKey="review" type="number" domain={[1, 'dataMax']} allowDecimals={false} tick={{ fontSize: 11 }} />
              <YAxis
                scale="log"
                domain={[MIN_INTERVAL_DAYS, yMax]}
                ticks={Y_TICKS}
                allowDataOverflow
                tick={{ fontSize: 11 }}
              />
              <Tooltip
                formatter={(value) => [t('reviewPage.intervalChart.days', { count: Math.round(Number(value) * 10) / 10 }), '']}
                labelFormatter={(review) => t('reviewPage.intervalChart.reviewN', { n: review })}
              />
              <Line
                type="monotone"
                dataKey="days"
                stroke={theme.palette.text.disabled}
                strokeWidth={1.5}
                isAnimationActive={false}
                dot={(props: { cx?: number; cy?: number; payload?: { rating: ReviewRating; review: number } }) => {
                  const { cx, cy, payload } = props;
                  if (cx === undefined || cy === undefined || !payload) return <g key="empty" />;
                  return <circle key={payload.review} cx={cx} cy={cy} r={4} fill={ratingColor[payload.rating]} />;
                }}
              />
            </LineChart>
          </ResponsiveContainer>
        </Box>
      )}

      {hasPartialHistory && (
        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', textAlign: 'center', mt: 0.5 }}>
          {t('reviewPage.intervalChart.partial')}
        </Typography>
      )}
    </Box>
  );
}
